import React, { useCallback, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { useCreateConnectivityAction } from '~/hooks/apis/price-parity/useCreateConnectivityAction';
import { useTriggerExtranetNotification } from '~/hooks/apis/price-parity/useTriggerExtranetNotification';

interface Props {
  propertyId: string;
  propertyName: string;
  vendorBk: string | null;
  connectivityActionStatus: string | null;
}

export default function ControlCentreActionButtons({
  propertyId,
  propertyName,
  vendorBk,
  connectivityActionStatus,
}: Props) {
  const showConfirmDialog = useConfirmDialog();
  const createAction = useCreateConnectivityAction();
  const triggerNotification = useTriggerExtranetNotification();

  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);

  const hasExistingAction = connectivityActionStatus !== null;
  const canMutate = vendorBk != null;

  const handleCreateAction = useCallback(async () => {
    if (!canMutate) return;

    const confirmed = await showConfirmDialog({
      title: 'Create Connectivity Action?',
      description: `Are you sure you want to create a new connectivity action for ${propertyName}? This will create a follow-up task for the Connectivity team to address parity issues.`,
    });

    if (!confirmed) {
      return;
    }

    createAction.mutate(propertyId);
  }, [showConfirmDialog, propertyId, propertyName, vendorBk, canMutate, createAction]);

  const handleConfirmNotification = useCallback(() => {
    if (!canMutate) return;
    setNotificationDialogOpen(false);
    triggerNotification.mutate(propertyId);
  }, [propertyId, vendorBk, canMutate, triggerNotification]);

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ py: 1, flexWrap: 'nowrap' }}>
        <Button
          size="small"
          variant="contained"
          onClick={handleCreateAction}
          disabled={!canMutate || hasExistingAction || createAction.isPending}
          sx={{
            minWidth: 0,
            px: 1.5,
            py: 0.75,
            whiteSpace: 'nowrap',
          }}
        >
          {createAction.isPending ? 'Creating...' : 'Create Connectivity Action'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setNotificationDialogOpen(true)}
          disabled={!canMutate || triggerNotification.isPending}
          sx={{
            minWidth: 0,
            px: 1.5,
            py: 0.75,
            whiteSpace: 'nowrap',
          }}
        >
          {triggerNotification.isPending ? 'Sending...' : 'Notify Partner'}
        </Button>
      </Stack>

      <Dialog open={notificationDialogOpen} onClose={() => setNotificationDialogOpen(false)}>
        <DialogTitle>Trigger Extranet Notification?</DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <DialogContentText>
            Are you sure you want to send a parity notification to <strong>{propertyName}</strong>?
          </DialogContentText>
          <Typography variant="body2" sx={{ mt: 2 }}>
            This will:
          </Typography>
          <Typography component="ul" variant="body2" sx={{ mt: 0.5, pl: 2 }}>
            <li>Create a connectivity action</li>
            <li>Send an automated notification to the hotel partner via Extranet</li>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmNotification}>
            Send Notification
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
